import React, { useState } from 'react';
import {
  FINANCIAL_EXPERIENCE_OPTIONS,
  MONEY_STAGE_OPTIONS,
  MONTHLY_SAVINGS_OPTIONS,
  AGE_RANGE_OPTIONS
} from '../config/onboardingQuestions';
import { UserIcon, GlobeIcon, TrendingUpIcon, PiggyBankIcon, CalendarIcon, BellIcon, LogOutIcon } from './Icons';
import styles from '../styles/SettingsScreen.module.css';

/**
 * Settings/Profile Screen
 * Allows users to view and edit their profile information
 * All fields from onboarding are editable here
 *
 * Props:
 * - user: User object with all profile fields
 * - onUpdateUser: Callback to update user data
 * - onLogout: Logout callback
 * - onClose: Close settings screen
 */
function SettingsScreen({ user, onUpdateUser, onLogout, onClose }) {
  const [isSaving, setIsSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [customCountry, setCustomCountry] = useState('');

  if (!user) return null;

  const handleEdit = (field, currentValue) => {
    setEditingField(field);
    if (field === 'countries') {
      setTempValue(currentValue || []);
    } else {
      setTempValue(currentValue || '');
    }
  };

  const handleSave = async (field) => {
    setIsSaving(true);
    try {
      // For countries field, convert array to comma-separated string
      const valueToSave = field === 'countries' && Array.isArray(tempValue)
        ? tempValue.join(', ')
        : tempValue;

      await onUpdateUser({ [field]: valueToSave });
      setEditingField(null);
      setTempValue('');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue('');
    setCustomCountry('');
  };

  // Country options
  const COUNTRY_OPTIONS = [
    'Singapore',
    'Hong Kong',
    'United Kingdom',
    'Australia',
    'United States'
  ];

  // Toggle country in array
  const toggleCountry = (country) => {
    const current = Array.isArray(tempValue) ? tempValue : [];
    if (current.includes(country)) {
      setTempValue(current.filter(c => c !== country));
    } else {
      setTempValue([...current, country]);
    }
  };

  // Add custom country
  const addCustomCountry = () => {
    if (!customCountry.trim()) return;
    const current = Array.isArray(tempValue) ? tempValue : [];
    if (!current.includes(customCountry.trim())) {
      setTempValue([...current, customCountry.trim()]);
    }
    setCustomCountry('');
  };

  // Remove country
  const removeCountry = (country) => {
    const current = Array.isArray(tempValue) ? tempValue : [];
    setTempValue(current.filter(c => c !== country));
  };

  // Parse countries (comma-separated string, JSON string, or array)
  const parseCountries = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    // Try parsing as JSON first (for legacy data)
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // Not JSON, continue
    }

    // Parse as comma-separated string (current format)
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(s => s);
    }

    return [];
  };

  const userCountries = parseCountries(user.countries);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={onClose} className={styles.backButton}>
          ← Back
        </button>
        <h1 className={styles.title}>Settings</h1>
        <div style={{ width: '60px' }}></div> {/* Spacer for center alignment */}
      </div>

      <div className={styles.content}>
        {/* User Name Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <UserIcon size={20} />
            <h2 className={styles.sectionTitle}>Profile</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Name</label>
            <div className={styles.value}>
              {user.firstname && user.lastname
                ? `${user.firstname} ${user.lastname}`
                : user.name || 'Not set'}
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <div className={styles.value}>{user.email || 'Not set'}</div>
          </div>
        </section>

        {/* Countries */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <GlobeIcon size={20} />
            <h2 className={styles.sectionTitle}>Location</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Relevant Countries</label>
            {editingField !== 'countries' ? (
              <>
                <div className={styles.value}>
                  {userCountries.length > 0 ? userCountries.join(', ') : 'Not set'}
                </div>
                <button
                  onClick={() => handleEdit('countries', userCountries)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <div className={styles.checkboxGroup}>
                  {COUNTRY_OPTIONS.map(country => (
                    <label key={country} className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={tempValue.includes(country)}
                        onChange={() => toggleCountry(country)}
                        className={styles.checkbox}
                      />
                      {country}
                    </label>
                  ))}
                </div>

                {/* Custom country input */}
                <div className={styles.customInput}>
                  <input
                    type="text"
                    value={customCountry}
                    onChange={(e) => setCustomCountry(e.target.value)}
                    placeholder="Other country..."
                    className={styles.input}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addCustomCountry();
                      }
                    }}
                  />
                  <button onClick={addCustomCountry} className={styles.addButton}>
                    Add
                  </button>
                </div>

                {/* Show selected custom countries */}
                {tempValue.filter(c => !COUNTRY_OPTIONS.includes(c)).length > 0 && (
                  <div className={styles.selectedCountries}>
                    {tempValue.filter(c => !COUNTRY_OPTIONS.includes(c)).map(country => (
                      <span key={country} className={styles.countryTag}>
                        {country}
                        <button
                          onClick={() => removeCountry(country)}
                          className={styles.removeButton}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('countries')}
                    disabled={isSaving}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Financial Experience */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <TrendingUpIcon size={20} />
            <h2 className={styles.sectionTitle}>Investment Experience</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Experience Level</label>
            {editingField !== 'financialExperience' ? (
              <>
                <div className={styles.value}>{user.financialExperience || 'Not set'}</div>
                <button
                  onClick={() => handleEdit('financialExperience', user.financialExperience)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <div className={styles.radioGroup}>
                  {FINANCIAL_EXPERIENCE_OPTIONS.map(option => (
                    <label key={option.value} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="financialExperience"
                        value={option.value}
                        checked={tempValue === option.value}
                        onChange={(e) => setTempValue(e.target.value)}
                        className={styles.radio}
                      />
                      <div>
                        <div className={styles.optionLabel}>{option.label}</div>
                        <div className={styles.optionDescription}>{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('financialExperience')}
                    disabled={isSaving || !tempValue}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Money Stage */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <PiggyBankIcon size={20} />
            <h2 className={styles.sectionTitle}>Money Journey</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Current Stage</label>
            {editingField !== 'moneyStage' ? (
              <>
                <div className={styles.value}>
                  {user.moneyStage
                    ? MONEY_STAGE_OPTIONS.find(o => o.value === user.moneyStage)?.label || user.moneyStage
                    : 'Not set'}
                </div>
                <button
                  onClick={() => handleEdit('moneyStage', user.moneyStage)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <div className={styles.radioGroup}>
                  {MONEY_STAGE_OPTIONS.map(option => (
                    <label key={option.value} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="moneyStage"
                        value={option.value}
                        checked={tempValue === option.value}
                        onChange={(e) => setTempValue(e.target.value)}
                        className={styles.radio}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('moneyStage')}
                    disabled={isSaving || !tempValue}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Monthly Savings */}
        <section className={styles.section}>
          <div className={styles.field}>
            <label className={styles.label}>Monthly Savings (US$)</label>
            {editingField !== 'monthlySavings' ? (
              <>
                <div className={styles.value}>
                  {user.monthlySavings !== null && user.monthlySavings !== undefined
                    ? MONTHLY_SAVINGS_OPTIONS.find(o => o.value === user.monthlySavings)?.label || 'Not set'
                    : 'Not set'}
                </div>
                <button
                  onClick={() => handleEdit('monthlySavings', user.monthlySavings)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <div className={styles.radioGroup}>
                  {MONTHLY_SAVINGS_OPTIONS.map(option => (
                    <label key={option.value} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="monthlySavings"
                        value={option.value}
                        checked={tempValue === option.value}
                        onChange={(e) => setTempValue(parseInt(e.target.value))}
                        className={styles.radio}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('monthlySavings')}
                    disabled={isSaving || tempValue === ''}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Age Range */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <CalendarIcon size={20} />
            <h2 className={styles.sectionTitle}>Demographics</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Age Range</label>
            {editingField !== 'ageRange' ? (
              <>
                <div className={styles.value}>{user.ageRange || 'Not set'}</div>
                <button
                  onClick={() => handleEdit('ageRange', user.ageRange)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <div className={styles.radioGroup}>
                  {AGE_RANGE_OPTIONS.map(option => (
                    <label key={option.value} className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="ageRange"
                        value={option.value}
                        checked={tempValue === option.value}
                        onChange={(e) => setTempValue(e.target.value)}
                        className={styles.radio}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('ageRange')}
                    disabled={isSaving || !tempValue}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Contact Instructions */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <BellIcon size={20} />
            <h2 className={styles.sectionTitle}>Notifications</h2>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Preferred Contact Schedule</label>
            {editingField !== 'contactInstructions' ? (
              <>
                <div className={styles.value}>{user.contactInstructions || 'Not set'}</div>
                <button
                  onClick={() => handleEdit('contactInstructions', user.contactInstructions)}
                  className={styles.editButton}
                >
                  Edit
                </button>
              </>
            ) : (
              <div className={styles.editContainer}>
                <textarea
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder="Describe when you'd like to hear from Sophia..."
                  className={styles.textarea}
                  rows={4}
                />
                <div className={styles.buttonGroup}>
                  <button
                    onClick={() => handleSave('contactInstructions')}
                    disabled={isSaving || !tempValue.trim()}
                    className={styles.saveButton}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Logout Section */}
        <section className={styles.section}>
          <button onClick={onLogout} className={styles.logoutButton}>
            <LogOutIcon size={20} />
            <span>Logout</span>
          </button>
        </section>
      </div>
    </div>
  );
}

export default SettingsScreen;
